import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Typography,
  Spin,
  Alert,
  Tag,
  Empty,
  Statistic,
  Row,
  Col,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { fetchClassRoomAttendance } from "../../store/attendanceSlice";

const { Title } = Typography;

const ClassRoomAttendancePage = () => {
  const dispatch = useDispatch();
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [attendanceSummary, setAttendanceSummary] = useState(null);

  // ✅ Get userInfo from session or localStorage
  const userInfo =
    JSON.parse(sessionStorage.getItem("userInfo")) ||
    JSON.parse(localStorage.getItem("userInfo")) ||
    {};

  // ✅ Determine studentId based on role
  useEffect(() => {
    if (
      Array.isArray(userInfo.roles) &&
      userInfo.roles.includes("Student") &&
      Array.isArray(userInfo.studentIds) &&
      userInfo.studentIds.length > 0
    ) {
      setSelectedStudentId(userInfo.studentIds[0]);
    } else if (
      Array.isArray(userInfo.roles) &&
      userInfo.roles.includes("Parent") &&
      Array.isArray(userInfo.studentIds) &&
      userInfo.studentIds.length > 0
    ) {
      setSelectedStudentId(userInfo.studentIds[0]); // Optionally allow selection
    }
  }, [userInfo]);

  const { classRoomAttendance, loading, error } = useSelector(
    (state) => state.attendance
  );

  // ✅ Fetch attendance when student ID changes
  useEffect(() => {
    if (selectedStudentId) {
      dispatch(fetchClassRoomAttendance(selectedStudentId)).then((res) => {
        if (
          res?.payload &&
          typeof res.payload === "object" &&
          "presentCount" in res.payload
        ) {
          setAttendanceSummary(res.payload);
        }
      });
    }
  }, [dispatch, selectedStudentId]);

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: "Room",
      dataIndex: ["scheduleSlot", "room", "roomName"],
      key: "room",
    },
    {
      title: "Period",
      key: "period",
      render: (_, record) =>
        record?.scheduleSlot?.schoolDay?.period?.periodName || "N/A",
    },
    {
      title: "Start Time",
      key: "startTime",
      render: (_, record) =>
        record?.scheduleSlot?.schoolDay?.period?.startTime?.slice(0, 5) || "-",
    },
    {
      title: "End Time",
      key: "endTime",
      render: (_, record) =>
        record?.scheduleSlot?.schoolDay?.period?.endTime?.slice(0, 5) || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === 1 ? "green" : "red"}>
          {status === 1 ? "Present" : "Absent"}
        </Tag>
      ),
    },
  ];

  return (
    <div style={{ padding: "10px" }}>
      <Title level={3} style={{ textAlign: "center", marginBottom: 20 }}>
        Classroom Attendance Report
      </Title>

      <Card
        style={{ maxWidth: "1000px", margin: "auto" }}
        bordered={false}
        bodyStyle={{ padding: "16px" }}
      >
        {loading ? (
          <Spin size="large" style={{ display: "block", margin: "auto" }} />
        ) : error ? (
          <Alert type="error" message={error} showIcon />
        ) : classRoomAttendance.length === 0 ? (
          <Empty description="No attendance records found." />
        ) : (
          <>
            {/* ✅ Attendance Summary */}
            {attendanceSummary && (
              <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col span={6}>
                  <Statistic
                    title="Total Records"
                    value={attendanceSummary.totalRecords}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Present"
                    value={attendanceSummary.presentCount}
                    valueStyle={{ color: "#3f8600" }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Absent"
                    value={attendanceSummary.absentCount}
                    valueStyle={{ color: "#cf1322" }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Tardy"
                    value={attendanceSummary.tardyCount}
                    valueStyle={{ color: "#faad14" }}
                  />
                </Col>
              </Row>
            )}

            <Table
              dataSource={classRoomAttendance}
              columns={columns}
              rowKey={(record) => record.accID}
              pagination={{ pageSize: 5 }}
              scroll={{ x: 600 }}
            />
          </>
        )}
      </Card>
    </div>
  );
};

export default ClassRoomAttendancePage;
